import { Parhato2OrderWithItems } from "./types";
import {
  convertCreateOrderToParhato2,
  convertParhato2OrderToBaseOrder,
  convertOrderReviewSB,
  convertSBOrderReview,
  convertParhato2OrderHistoryToBaseOrder,
  convertOrderTracking,
} from "./converters";
import {
  CreateOrderData,
  CreatePaymentPayload,
  OrderHistory,
  OrderReviewType,
  ORDERS_HISTORY_PARAMS,
  ORDERS_PARAMS,
  OrderTracking,
  OrderTrackingPayload,
  PaymentFormData,
  TOrder,
  BaseOrdersClient,
} from "@core/types";
import { SupaOrderReviews, SupaOrdersHistory, SupaOrderTracking } from "../types/supabaseFragments";
import { supabaseClient } from "../supabase";

class OrdersClient extends BaseOrdersClient {
  async getUnpaidOrdersId(): Promise<TOrder["id"][]> {
    if (!this.instance.token) return [];

    const { data } = await supabaseClient.functions.invoke("unpaid-orders");

    return data as TOrder["id"][];
  }

  async getOrders(params: ORDERS_PARAMS): Promise<TOrder[]> {
    const response = await this.instance.get<Parhato2OrderWithItems[]>("/wp-json/wc/v3/orders/", {
      params: { ...params },
    });

    return response.data.map(convertParhato2OrderToBaseOrder);
  }

  async getOrdersHistory(params: ORDERS_HISTORY_PARAMS): Promise<OrderHistory[]> {
    const response = await this.instance.get<SupaOrdersHistory[]>("/wp-json/parhato-api/v1/orders-history", {
      params: { ...params },
    });

    return response?.data ? response?.data.map(convertParhato2OrderHistoryToBaseOrder) : [];
  }

  async getOrderById(id: string): Promise<TOrder> {
    const response = await this.instance.get<Parhato2OrderWithItems>(`/wp-json/wc/v3/orders/${id}`);

    return convertParhato2OrderToBaseOrder(response.data);
  }

  async createOrder(orderData: CreateOrderData): Promise<TOrder> {
    const order = convertCreateOrderToParhato2(orderData);
    console.log({ order });

    const { data } = await this.instance.post<Parhato2OrderWithItems>("wp-json/parhato-api/v1/orders", order);

    return data && convertParhato2OrderToBaseOrder(data as Parhato2OrderWithItems);
  }

  async createPayment(payload: CreatePaymentPayload): Promise<PaymentFormData> {
    throw new Error("createPayment not implemented");
  }

  async getReviewSetOrderById(orderId: string): Promise<OrderReviewType | null> {
    const { data: orderReview } = await this.instance.get<SupaOrderReviews>(`/wp-json/wc/v3/orders/reviews`, {
      params: {
        orderId,
      },
    });

    return orderReview && convertSBOrderReview(orderReview);
  }

  async addReviewOrder(orderData: OrderReviewType): Promise<OrderReviewType | null> {
    const orderReview = convertOrderReviewSB(orderData);
    const { data } = await this.instance.post<SupaOrderReviews>(`/wp-json/wc/v3/orders/reviews`, orderReview);

    return data && convertSBOrderReview(data);
  }

  async getOrderTracking(orderReqData: OrderTrackingPayload): Promise<OrderTracking | null> {
    const { data } = await this.instance.post<SupaOrderTracking>(
      `/wp-json/parhato-api/v1/order-tracking`,
      orderReqData,
    );

    return data ? convertOrderTracking(data) : null;
  }

  async orderCancel(orderId: string): Promise<TOrder> {
    const { data } = await this.instance.post<Parhato2OrderWithItems>(`/wp-json/parhato-api/v1/order-cancel`, {
      orderId,
    });

    return data && convertParhato2OrderToBaseOrder(data);
  }

  async createTaxiOrder(orderId: TOrder["id"]): Promise<any> {
    const { data } = await supabaseClient.functions.invoke("create-taxi-order", {
      body: { orderId },
    });

    return data;
  }

  async getUnreviewedOrdersId(): Promise<TOrder["id"][]> {
    if (!this.instance.token) return [];

    const { data } = await supabaseClient.functions.invoke<{ ids: TOrder["id"][] }>("get-unreviewed-orders");

    return data?.ids || [];
  }
}

export default OrdersClient;
